import { Select, Input, Button, Empty } from 'antd';
import React, { useEffect, useState } from 'react'
import Table from '@/Modules/table/Table';
import UserInterestModal from '@/Components/UserInterestModal';
import { SorterResult } from 'antd/lib/table/interface';
import { useAppDispatch, useAppSelector } from '@/Hooks/useAppRedux';
import { AccountsInterestData } from '@/Interfaces/I_AccountsInterest';
import { getAccountsInterest, getAccountInterestMyPageTopics, updateAccountsInterest } from '@/Redux/slices/AccountsInterestSlice';

const { Search } = Input;

interface SorterDataType {
  name: string;
  pKey: number;
  creationDate: number;
}

const UserInterest = () => {
  const dispatch = useAppDispatch()
  const { loading } = useAppSelector((state) => state.accountsInterest);
  const { accountsInterest } = useAppSelector((state) => state.accountsInterest)
  const [ localAccountsInterest, setLocalAccountsInterest] = useState([] as AccountsInterestData[])
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPage, setTotalPage] = useState(0);
  const [searchOption, setSearchOption] = useState('hybrisId');
  const [keyword, setKeyword] = useState<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUserUuid, setSelectedUserUuid] = useState("");
  const [sortedInfo, setSortedInfo] = useState<SorterResult<SorterDataType>>({
    order: 'descend',
    columnKey: 'creationDate',
  });

  const handleOpenModal = (uuid: string) => {
    setSelectedUserUuid(uuid);
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setSelectedUserUuid("")
  }

  const handleConfirmClick = (userUuid: string, interests: number[]) => {
    const payload = {
      userUuid,
      interests
    }
    dispatch(updateAccountsInterest(payload))
    handleCloseModal()
  }

  useEffect(() => {
    const sort =
        sortedInfo.column !== undefined
          ? (sortedInfo.field as string)
              .replace(/[A-Z]/g, (letter) => `_${letter}`)
              .toUpperCase()
              .concat(sortedInfo.order === 'ascend' ? '_ASC' : '_DESC')
          : 'CREATION_DATE_DESC';
      dispatch(
        getAccountsInterest({
          hybris_id: searchOption === 'hybrisId' ? keyword.trim() : null,
          name: searchOption === 'name' ? keyword.trim() : null,
          uuid: searchOption === 'uuid' ? keyword.trim() : null,
          page_no: currentPage - 1,
          page_size: pageSize,
          sort,
        }),
      );
  }, [
    dispatch,
    keyword,
    currentPage,
    pageSize,
    sortedInfo.field,
    sortedInfo.order,
    sortedInfo.column,
    searchOption
  ])

  useEffect(() => {
    if (!loading && accountsInterest.data) {
      setLocalAccountsInterest(accountsInterest.data)
      setTotalPage(accountsInterest.pagination.total);
    }
  }, [loading, accountsInterest.data, accountsInterest.pagination.total])

  useEffect(() => {
    dispatch(getAccountInterestMyPageTopics())
  }, [dispatch])

  const searchOptions = [
    {
      value: 'hybrisId',
      label: 'Pkey',
    },
    {
      value: 'name',
      label: 'User Name',
    },
    {
      value: 'uuid',
      label: 'User UUID',
    },
  ];

  const columns = [
    {
      title: 'User Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <div className="flex items-center justify-start">
          <p className="h-6 line-clamp-1 break-all m-0">{text}</p>
        </div>
      ),
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'name' ? sortedInfo.order : null,
    },
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
    },
    {
      title: 'PKey',
      dataIndex: 'hybrisId',
      key: 'hybrisId',
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'hybrisId' ? sortedInfo.order : null,
    },
    {
      title: 'Interest',
      dataIndex: 'uuid',
      key: 'interest',
      render: (uuid: string) => (
        <Button onClick={() => handleOpenModal(uuid)}>Edit</Button>
      )
    }
  ]

  return (
    <div className="h-auto bg-white p-6 pb-2">
      <div className="flex pb-4">
        <div className="flex ml-auto items-center">
          <div className="pr-4">
            <Select
              style={{ width: 200 }}
              defaultValue={searchOption}
              options={searchOptions}
              onChange={(value) => {
                setSearchOption(value);
              }}
              bordered={false}
            />
          </div>
          <Search
            placeholder="input text"
            allowClear
            onSearch={(value) => {
              setKeyword(value.trim());
              setCurrentPage(1);
            }}
            style={{ width: 264 }}
            className="pr-4"
          />
        </div>
      </div>
      {!loading && accountsInterest.data.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Data" />
      ) : (
      <Table
          className=""
          columns={columns}
          dataSource={localAccountsInterest}
          loading={loading}
          pagination={{
            total: totalPage,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            defaultPageSize: pageSize,
            defaultCurrent: 1,
            showSizeChanger: true,
            current: currentPage,
            onChange: (page: number, selectPageSize: number) => {
              setCurrentPage(page);
              setTotalPage(selectPageSize);
            },
            onShowSizeChange: (current, size) => {
              setPageSize(size);
            },
          }}
          onChange={(pagination, filters, sorter) => {
            const sorterInfo = sorter as SorterResult<SorterDataType>;
            setSortedInfo(sorterInfo);
          }}
        />
      )}
      <UserInterestModal 
        open={modalOpen}
        userUuid={selectedUserUuid}
        closeModal={handleCloseModal}
        onConfirmClick={handleConfirmClick}
      />
    </div>
  )
}

export default UserInterest;